/**
 * Wochen-Range-Picker: Kalenderansicht, bei der ganze Wochen ausgewählt werden.
 * 1. Klick = Startwoche (Montag), 2. Klick = Endwoche (Sonntag).
 * Hover hebt die gesamte Woche hervor.
 */

type OnChangeFn = (startIso: string, endIso: string) => void;

/** ISO-String aus lokalem Datum (kein UTC-Versatz) */
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** ISO-String als lokales Datum parsen (kein UTC-Versatz).
 *  Unterstützt "YYYY-MM-DD" und "YYYY-MM-DDTHH:..." (ISO full). */
function parseDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

function mondayOf(d: Date): Date {
  const r = new Date(d);
  const day = r.getDay(); // 0=So, 1=Mo, ...
  const diff = day === 0 ? -6 : 1 - day;
  r.setDate(r.getDate() + diff);
  r.setHours(0, 0, 0, 0);
  return r;
}

function sundayOf(d: Date): Date {
  const mon = mondayOf(d);
  const r = new Date(mon);
  r.setDate(mon.getDate() + 6);
  return r;
}

function fmtDisplay(d: Date): string {
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

function getISOWeek(d: Date): number {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  dt.setUTCDate(dt.getUTCDate() + 4 - (dt.getUTCDay() || 7));
  const y = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil(((dt.getTime() - y.getTime()) / 86400000 + 1) / 7);
}

const DE_MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember"];
const DE_DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export function renderWeekRangePicker(
  container: HTMLElement,
  opts?: { startDate?: string; endDate?: string },
  onChange?: OnChangeFn
): void {
  // ── State ──
  let startMon: Date | null = opts?.startDate ? mondayOf(parseDate(opts.startDate)) : null;
  let endSun: Date | null = opts?.endDate ? sundayOf(parseDate(opts.endDate)) : null;

  // Startet mit dem Monat des Startdatums, sonst aktueller Monat
  const initialDate = startMon ?? new Date();
  let viewYear = initialDate.getFullYear();
  let viewMonth = initialDate.getMonth(); // 0-basiert

  function render() {
    const firstDay = new Date(viewYear, viewMonth, 1);
    // Wochentag des 1. (Mo=0 ... So=6 in unserem Raster)
    const startDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

    // Kalender-Zellen aufbauen (immer 6 Reihen × 7 Spalten, damit die Höhe konstant bleibt)
    const rows: Date[][] = [];
    let dayNum = 1 - startDow; // kann negativ sein (Tage aus Vormonat)
    for (let row = 0; row < 6; row++) {
      const week: Date[] = [];
      for (let col = 0; col < 7; col++) {
        const d = new Date(viewYear, viewMonth, dayNum);
        week.push(d);
        dayNum++;
      }
      rows.push(week);
    }

    // Zeilen-HTML bauen
    const rowsHtml = rows.map(week => {
      const mon = mondayOf(week[0]);
      const sun = sundayOf(week[0]);
      const weekIso = isoDate(mon);

      let trClass = "";
      if (startMon && isoDate(mon) === isoDate(startMon)) trClass = "wrp-start";
      else if (endSun && isoDate(sun) === isoDate(endSun)) trClass = "wrp-end";
      else if (startMon && endSun && mon > startMon && sun < endSun) trClass = "wrp-in-range";

      const cells = week.map((d, col) => {
        const isOther = d.getMonth() !== viewMonth;
        return `<td class="${isOther ? "wrp-other-month" : ""}" data-col="${col}">${d.getDate()}</td>`;
      }).join("");

      const kw = getISOWeek(mon);
      return `<tr class="${trClass}" data-week="${weekIso}"><td class="wrp-kw">KW ${kw}</td>${cells}</tr>`;
    }).join("");

    // Auswahl-Anzeige
    let displayText = "";
    if (startMon && endSun) {
      displayText = `${fmtDisplay(startMon)} – ${fmtDisplay(endSun)}`;
    } else if (startMon) {
      displayText = `${fmtDisplay(startMon)} – ?`;
    }

    container.innerHTML = `
      <div class="wrp">
        <div class="wrp-header">
          <button type="button" class="wrp-nav" id="wrp-prev">‹</button>
          <span>${DE_MONTHS[viewMonth]} ${viewYear}</span>
          <button type="button" class="wrp-nav" id="wrp-next">›</button>
        </div>
        <table>
          <thead>
            <tr><th class="wrp-kw-th"></th>${DE_DAYS.map(d => `<th>${d}</th>`).join("")}</tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
        <div class="wrp-display">${displayText}</div>
      </div>`;

    // Navigation
    container.querySelector("#wrp-prev")!.addEventListener("click", () => {
      viewMonth--;
      if (viewMonth < 0) { viewMonth = 11; viewYear--; }
      render();
    });
    container.querySelector("#wrp-next")!.addEventListener("click", () => {
      viewMonth++;
      if (viewMonth > 11) { viewMonth = 0; viewYear++; }
      render();
    });

    // Klick-Handler auf Zeilen
    container.querySelectorAll<HTMLTableRowElement>("tbody tr").forEach(tr => {
      tr.addEventListener("click", () => {
        const weekIsoStr = tr.dataset.week!;
        const clicked = mondayOf(parseDate(weekIsoStr));
        const clickedSun = sundayOf(parseDate(weekIsoStr));

        if (!startMon || (startMon && endSun)) {
          // Neu starten
          startMon = clicked;
          endSun = null;
        } else {
          // End-Woche setzen
          if (clicked >= startMon) {
            endSun = clickedSun;
            onChange?.(isoDate(startMon), isoDate(endSun));
          } else {
            // Geklickt vor Start → neuen Start setzen
            startMon = clicked;
            endSun = null;
          }
        }
        render();
      });
    });
  }

  render();
}
